import type { ModuleMigrationDef } from "@kubdee/sdk";

// ============================================================================
// Marketing module — 001_initial
//
// Provider-agnostic social publishing data model (#786). Seven tables that
// hold campaigns, content posts, per-channel fan-out targets, media library,
// audit logs, and engagement snapshots.
//
// Cross-module FK target: `marketing_post_targets.connector_instance_id`
// references `connectors_instances.id` — the prefixed table name is resolved
// via `crossModuleFk("connectors", "instances")` in `schemas/post-targets.ts`
// and matches the hard-coded value below. CASCADE is intentionally NOT
// declared on this cross-module reference: deleting a connector instance
// should NOT silently wipe marketing history. The connectors module's soft-
// delete flow + the dispatcher's ownership check handle integrity.
// ============================================================================
export const migration: ModuleMigrationDef = {
  key: "001_initial",
  version: "0.1.0",
  sql: `
CREATE TABLE IF NOT EXISTS \`marketing_campaigns\` (
\t\`id\` text PRIMARY KEY NOT NULL,
\t\`company_id\` text NOT NULL,
\t\`name\` text NOT NULL,
\t\`description\` text,
\t\`status\` text DEFAULT 'draft' NOT NULL,
\t\`starts_at\` text,
\t\`ends_at\` text,
\t\`created_at\` text NOT NULL,
\t\`created_by\` text,
\t\`updated_at\` text NOT NULL,
\t\`updated_by\` text,
\t\`deleted_at\` text,
\t\`deleted_by\` text
);
CREATE INDEX IF NOT EXISTS \`marketing_campaigns_company_id_idx\` ON \`marketing_campaigns\` (\`company_id\`);
CREATE INDEX IF NOT EXISTS \`marketing_campaigns_company_status_idx\` ON \`marketing_campaigns\` (\`company_id\`, \`status\`);
CREATE TABLE IF NOT EXISTS \`marketing_content_posts\` (
\t\`id\` text PRIMARY KEY NOT NULL,
\t\`company_id\` text NOT NULL,
\t\`campaign_id\` text,
\t\`caption\` text NOT NULL,
\t\`link_url\` text,
\t\`status\` text DEFAULT 'draft' NOT NULL,
\t\`scheduled_for\` text,
\t\`doc_status\` integer DEFAULT 0 NOT NULL,
\t\`submitted_at\` text,
\t\`submitted_by\` text,
\t\`created_at\` text NOT NULL,
\t\`created_by\` text,
\t\`updated_at\` text NOT NULL,
\t\`updated_by\` text,
\t\`deleted_at\` text,
\t\`deleted_by\` text,
\tFOREIGN KEY (\`campaign_id\`) REFERENCES \`marketing_campaigns\`(\`id\`) ON UPDATE no action ON DELETE no action
);
CREATE INDEX IF NOT EXISTS \`marketing_content_posts_company_id_idx\` ON \`marketing_content_posts\` (\`company_id\`);
CREATE INDEX IF NOT EXISTS \`marketing_content_posts_company_status_scheduled_idx\` ON \`marketing_content_posts\` (\`company_id\`, \`status\`, \`scheduled_for\`);
CREATE INDEX IF NOT EXISTS \`marketing_content_posts_campaign_id_idx\` ON \`marketing_content_posts\` (\`campaign_id\`);
CREATE TABLE IF NOT EXISTS \`marketing_media_assets\` (
\t\`id\` text PRIMARY KEY NOT NULL,
\t\`company_id\` text NOT NULL,
\t\`kind\` text NOT NULL,
\t\`url\` text NOT NULL,
\t\`mime_type\` text NOT NULL,
\t\`file_size_bytes\` integer NOT NULL,
\t\`width\` integer,
\t\`height\` integer,
\t\`duration_ms\` integer,
\t\`provider_media_id\` text,
\t\`alt_text\` text,
\t\`created_at\` text NOT NULL,
\t\`created_by\` text,
\t\`updated_at\` text NOT NULL,
\t\`updated_by\` text,
\t\`deleted_at\` text,
\t\`deleted_by\` text
);
CREATE INDEX IF NOT EXISTS \`marketing_media_assets_company_id_idx\` ON \`marketing_media_assets\` (\`company_id\`);
CREATE INDEX IF NOT EXISTS \`marketing_media_assets_company_kind_idx\` ON \`marketing_media_assets\` (\`company_id\`, \`kind\`);
CREATE TABLE IF NOT EXISTS \`marketing_post_targets\` (
\t\`id\` text PRIMARY KEY NOT NULL,
\t\`company_id\` text NOT NULL,
\t\`post_id\` text NOT NULL,
\t\`connector_instance_id\` text NOT NULL,
\t\`provider\` text NOT NULL,
\t\`provider_account_id\` text NOT NULL,
\t\`status\` text DEFAULT 'draft' NOT NULL,
\t\`provider_post_id\` text,
\t\`provider_url\` text,
\t\`error_code\` text,
\t\`error_message\` text,
\t\`attempts\` integer DEFAULT 0 NOT NULL,
\t\`last_attempted_at\` text,
\t\`published_at\` text,
\t\`idempotency_key\` text,
\t\`created_at\` text NOT NULL,
\t\`created_by\` text,
\t\`updated_at\` text NOT NULL,
\t\`updated_by\` text,
\t\`deleted_at\` text,
\t\`deleted_by\` text,
\tFOREIGN KEY (\`post_id\`) REFERENCES \`marketing_content_posts\`(\`id\`) ON UPDATE no action ON DELETE cascade,
\tFOREIGN KEY (\`connector_instance_id\`) REFERENCES \`connectors_instances\`(\`id\`) ON UPDATE no action ON DELETE no action
);
CREATE INDEX IF NOT EXISTS \`marketing_post_targets_company_id_idx\` ON \`marketing_post_targets\` (\`company_id\`);
CREATE INDEX IF NOT EXISTS \`marketing_post_targets_company_status_idx\` ON \`marketing_post_targets\` (\`company_id\`, \`status\`);
CREATE INDEX IF NOT EXISTS \`marketing_post_targets_post_id_idx\` ON \`marketing_post_targets\` (\`post_id\`);
CREATE INDEX IF NOT EXISTS \`marketing_post_targets_connector_instance_id_idx\` ON \`marketing_post_targets\` (\`connector_instance_id\`);
CREATE UNIQUE INDEX IF NOT EXISTS \`marketing_post_targets_company_idempotency_unique\` ON \`marketing_post_targets\` (\`company_id\`, \`idempotency_key\`);
CREATE TABLE IF NOT EXISTS \`marketing_post_media\` (
\t\`post_id\` text NOT NULL,
\t\`media_id\` text NOT NULL,
\t\`position\` integer DEFAULT 0 NOT NULL,
\tPRIMARY KEY (\`post_id\`, \`media_id\`),
\tFOREIGN KEY (\`post_id\`) REFERENCES \`marketing_content_posts\`(\`id\`) ON UPDATE no action ON DELETE cascade,
\tFOREIGN KEY (\`media_id\`) REFERENCES \`marketing_media_assets\`(\`id\`) ON UPDATE no action ON DELETE no action
);
CREATE INDEX IF NOT EXISTS \`marketing_post_media_media_id_idx\` ON \`marketing_post_media\` (\`media_id\`);
CREATE TABLE IF NOT EXISTS \`marketing_publish_logs\` (
\t\`id\` text PRIMARY KEY NOT NULL,
\t\`company_id\` text NOT NULL,
\t\`target_id\` text NOT NULL,
\t\`event\` text NOT NULL,
\t\`provider_post_id\` text,
\t\`provider_url\` text,
\t\`error_code\` text,
\t\`error_message\` text,
\t\`payload_json\` text,
\t\`created_at\` text NOT NULL,
\t\`created_by\` text,
\tFOREIGN KEY (\`target_id\`) REFERENCES \`marketing_post_targets\`(\`id\`) ON UPDATE no action ON DELETE cascade
);
CREATE INDEX IF NOT EXISTS \`marketing_publish_logs_company_id_idx\` ON \`marketing_publish_logs\` (\`company_id\`);
CREATE INDEX IF NOT EXISTS \`marketing_publish_logs_target_created_idx\` ON \`marketing_publish_logs\` (\`target_id\`, \`created_at\`);
CREATE INDEX IF NOT EXISTS \`marketing_publish_logs_company_created_idx\` ON \`marketing_publish_logs\` (\`company_id\`, \`created_at\`);
CREATE TABLE IF NOT EXISTS \`marketing_post_insights\` (
\t\`id\` text PRIMARY KEY NOT NULL,
\t\`company_id\` text NOT NULL,
\t\`target_id\` text NOT NULL,
\t\`snapshotted_at\` text NOT NULL,
\t\`impressions\` integer DEFAULT 0 NOT NULL,
\t\`reach\` integer DEFAULT 0 NOT NULL,
\t\`likes\` integer DEFAULT 0 NOT NULL,
\t\`comments\` integer DEFAULT 0 NOT NULL,
\t\`shares\` integer DEFAULT 0 NOT NULL,
\t\`clicks\` integer DEFAULT 0 NOT NULL,
\t\`engagement_rate\` real DEFAULT 0 NOT NULL,
\t\`raw_payload\` text,
\t\`created_at\` text NOT NULL,
\tFOREIGN KEY (\`target_id\`) REFERENCES \`marketing_post_targets\`(\`id\`) ON UPDATE no action ON DELETE cascade
);
CREATE INDEX IF NOT EXISTS \`marketing_post_insights_company_id_idx\` ON \`marketing_post_insights\` (\`company_id\`);
CREATE INDEX IF NOT EXISTS \`marketing_post_insights_target_snapshot_idx\` ON \`marketing_post_insights\` (\`target_id\`, \`snapshotted_at\`);
  `.trim(),
};
